import React, { useState } from 'react'
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import { useGetStoreSalesQuery, useDeleteStoreSaleMutation } from '../../slices/storeSalesApiSlice'
import { toast } from 'react-toastify'
import { useGetOpeningStoreStocksQuery } from '../../slices/openingStoreStocksApiSlice'; // For qty and itemCost
import { useGetStoreSuppliesQuery } from '../../slices/storeSuppliesApiSlice'; // For Pqty and Pcost
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const StoreSalesListScreen = () => {
  // UI state
  const [filter, setFilter] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const { data: openingStoreStockData } = useGetOpeningStoreStocksQuery();
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();


  // Data fetching
  const { data, isLoading, error, refetch } = useGetStoreSalesQuery(pageNumber)
  const [deleteStoreSale, { isLoading: loadingDelete }] = useDeleteStoreSaleMutation()


  // Delete handler
  const deleteHandler = async (id) => {
    if (window.confirm('Delete this storeSale?')) {
      try {
        await deleteStoreSale(id).unwrap()
        toast.success('StoreSale deleted')
        refetch()
      } catch (err) {
        toast.error(err?.data?.message || 'Delete failed')
      }
    }
  }

  // 1) Date‐range filter, 2) general filter (note/user/customer/product), 
  // 3) product‐only filter
  const filterStoreSales = () => {
    if (!data?.storeSales) return []

    // 1) date range
    let result = data.storeSales.filter((storeSale) => {
      const d = new Date(storeSale.date)
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate   || d <= new Date(endDate))
      )
    })

    // 2) general text filter
    if (filter.trim()) {
      const lf = filter.toLowerCase()
      result = result.filter((storeSale) => {
        return (
          storeSale.note?.toLowerCase().includes(lf) ||
          storeSale.username?.toLowerCase().includes(lf) ||
          storeSale.customerDetails?.name?.toLowerCase().includes(lf) ||
          storeSale.storeSaleItems.some((it) => it.name.toLowerCase().includes(lf))
        )
      })
    }

    // 3) product‐only filter
    if (productSearch.trim()) {
      const lp = productSearch.toLowerCase()
      result = result
        .map((storeSale) => ({
          ...storeSale,
          storeSaleItems: storeSale.storeSaleItems.filter((it) =>
            it.name.toLowerCase().includes(lp)
          ),
        }))
        .filter((storeSale) => storeSale.storeSaleItems.length > 0)
    }

    return result
  }

  // Total calculation
  /*const totalStoreSales = filterStoreSales().reduce((sum, storeSale) => {
    return (
      sum +
      storeSale.storeSaleItems.reduce(
        (sub, it) => sub + it.qty * it.price,
        0
      )
    )
  }, 0)*/

const calculatePqty = (productName) => {
    return storeSuppliesData?.storeSupplies?.reduce((totalQty, storeSupply) => {
      const productItems = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
      const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
      return totalQty + productQty;
    }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return storeSuppliesData?.storeSupplies?.reduce((totalCost, storeSupply) => {
      const productItems = storeSupply.storeSupplyItems.filter((item) => item.name === productName);
      const productCost = productItems.reduce((cost, item) => cost + item.itemCost, 0);
      return totalCost + productCost;
    }, 0) || 0;
  };

  const calculateAverageCost = (productName) => {
    const openingStoreStockItems = openingStoreStockData?.openingStoreStocks
      ?.flatMap((openingStoreStock) => openingStoreStock.openingStoreStockItems)
      ?.filter((item) => item.name === productName);

    const qty = openingStoreStockItems?.reduce((total, item) => total + item.qty, 0) || 0;
    const itemCost = openingStoreStockItems?.reduce((total, item) => total + item.itemCost, 0) || 0;

    const Pqty = calculatePqty(productName);
    const Pcost = calculatePcost(productName);

    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0;
  };
  
  const calculateTotals = () => {
  const filteredStoreSales = filterStoreSales();

  const totalStoreSales = filteredStoreSales.reduce(
    (total, storeSale) =>
      total + storeSale.storeSaleItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      ),
    0
  );

  const totalQty = filteredStoreSales.reduce(
    (total, storeSale) =>
      total + storeSale.storeSaleItems.reduce((subTotal, item) => subTotal + item.qty, 0),
    0
  );

  const totalCost = filteredStoreSales.reduce((total, storeSale) => {
    return total + storeSale.storeSaleItems.reduce((subTotal, item) => {
      const averageCost = calculateAverageCost(item.name);
      return subTotal + averageCost * item.qty;
    }, 0);
  }, 0);

  const totalProfit = totalStoreSales - totalCost;

  return { totalStoreSales, totalCost, totalProfit, totalQty };
};

const { totalStoreSales, totalCost, totalProfit, totalQty } = calculateTotals();

const exportToExcel = () => {
  const filteredStoreSales = filterStoreSales();

  const rows = filteredStoreSales.flatMap((storeSale) =>
    storeSale.storeSaleItems.map((item) => ({
      Date: new Date(storeSale.date).toLocaleDateString(),
      Product: item.name,
      Quantity: item.qty,
      Price: item.price,
      Total: item.qty * item.price,
      User: storeSale.username || 'N/A',
      Note: storeSale.note || 'N/A',
      Customer: storeSale.customerDetails?.name || 'N/A',
    }))
  );

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'StoreSales');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, `StoreSales_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
};
  
  // Pagination handler
  const goPage = (p) => setPageNumber(p)

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>MiniSales</h1></Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter note/user/customer or any product"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
      <Row className="mb-3">
  <Col>
    <Button variant="success" onClick={exportToExcel}>
      Export to Excel
    </Button>
    <Link to='/StoreAdmin/storeSalesearchreplace' className='btn btn-light my-3'>
      Search
    </Link>

  </Col>
</Row>


      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
      </Row>

      {/* Totals Section */}
      <Row className="mb-3">
        <Col md={3}>
          <h4>Total StoreSales: ${totalStoreSales.toFixed(2)}</h4>
        </Col>
        <Col md={3}>
          <h4>Total Qty: ${totalQty.toFixed(2)}</h4>
        </Col>
        <Col md={3}>
          <h4>Total Cost: ${totalCost.toFixed(2)}</h4>
        </Col>
        <Col md={3} className="text-end">
          <h4>Total Profit: ${totalProfit.toFixed(2)}</h4>
        </Col>
      </Row>


      {/* New product‐only filter */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error fetching storeSales'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>USER</th>
                <th>NOTE</th>
                <th>CUSTOMER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterStoreSales().map((storeSale) =>
                storeSale.storeSaleItems.map((item) => (
                  <tr key={`${storeSale._id}-${item.product}`}>
                    <td>
                      {new Date(storeSale.date).toLocaleDateString()}
                    </td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>
                      D{(item.qty * item.price).toFixed(2)}
                    </td>
                    <td>{storeSale.username || 'N/A'}</td>
                    <td>{storeSale.note || 'N/A'}</td>
                    <td>
                      {storeSale.customerDetails.name || 'N/A'}
                    </td>
                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt=""
                          style={{
                            width: 10,
                            height: 10,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/storeSale/${storeSale._id}`}
                        variant="light"
                        size="sm"
                      >
                        Details
                      </Button>
                      <Button
                          as={Link}
                          to={`/storeSale/${storeSale._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                          
                        >
                          Edit
                        </Button>
                        <Button
                          as={Link}
                          to={`/storeSale/${storeSale._id}/copy`}
                          variant="light"
                          className="btn-sm mx-2"
                          
                        >
                          Copy
                        </Button>
                      <Button
                       
                        variant="danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => deleteHandler(storeSale._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => goPage(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default StoreSalesListScreen
